// @lat: [[tasks#SEO Tasks#Frontend]]
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ListChecks, Plus, Search, X, ArrowUp, ArrowDown, ChevronsUpDown, MessageSquare } from "lucide-react";
import * as api from "../lib/api";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../components/ui/dropdown-menu";
import FacetFilter from "../components/tasks/FacetFilter";
import TaskPicker from "../components/tasks/TaskPicker";
import TaskDrawer from "../components/tasks/TaskDrawer";
import SuggestedTab from "../components/tasks/SuggestedTab";
import MapPlanTab from "../components/tasks/MapPlanTab";
import SitemapPlanTab from "../components/tasks/SitemapPlanTab";
import {
  PRIMARY, STATUS_META, PRIORITY_META, StatusLabel, PriorityLabel, ActionBadge, MemberAvatar,
  useTaskMembers, relativeTime,
} from "../components/tasks/shared";

const PRIORITY_RANK = { urgent: 0, high: 1, medium: 2, low: 3 };
const STATUS_RANK = Object.fromEntries(Object.keys(STATUS_META).map((s, i) => [s, i]));
const CLOSED = new Set(["done", "canceled"]);

function readPref(key, fallback) {
  try { const v = localStorage.getItem(`tasks:${key}`); return v == null ? fallback : JSON.parse(v); } catch { return fallback; }
}
function writePref(key, value) {
  try { localStorage.setItem(`tasks:${key}`, JSON.stringify(value)); } catch {}
}

function SortHead({ col, label, sort, onSort, className = "" }) {
  const active = sort.col === col;
  const Icon = !active ? ChevronsUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead className={className}>
      <button onClick={() => onSort(col)} className="inline-flex items-center gap-1 text-[10px] tracking-[0.1em] uppercase font-bold text-zinc-400 hover:text-zinc-700">
        {label}
        <Icon size={11} className={active ? "text-zinc-700" : ""} />
      </button>
    </TableHead>
  );
}

export default function TasksPage() {
  const { key: openKey } = useParams();
  const navigate = useNavigate();
  const { members, byId, canEdit } = useTaskMembers();

  const [tab, setTab] = useState(() => readPref("tab", "tasks"));
  const [tasks, setTasks] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [suggestedCount, setSuggestedCount] = useState(null);
  const [planCount, setPlanCount] = useState(null);
  const [sitemapCount, setSitemapCount] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const [search, setSearch] = useState("");
  const [statusF, setStatusF] = useState(() => new Set(readPref("status", [])));
  const [priorityF, setPriorityF] = useState(() => new Set(readPref("priority", [])));
  const [assigneeF, setAssigneeF] = useState(() => new Set(readPref("assignee", [])));
  const [hideClosed, setHideClosed] = useState(() => readPref("hideClosed", true));
  const [sort, setSort] = useState(() => readPref("sort", { col: "priority", dir: "asc" }));

  useEffect(() => writePref("tab", tab), [tab]);
  useEffect(() => writePref("status", [...statusF]), [statusF]);
  useEffect(() => writePref("priority", [...priorityF]), [priorityF]);
  useEffect(() => writePref("assignee", [...assigneeF]), [assigneeF]);
  useEffect(() => writePref("hideClosed", hideClosed), [hideClosed]);
  useEffect(() => writePref("sort", sort), [sort]);

  const load = useCallback(async () => {
    try {
      const d = await api.getTasks();
      setTasks(d.tasks || []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load tasks.");
      setTasks(t => t || []);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(""), 3500);
    return () => clearTimeout(t);
  }, [notice]);

  const openTask = (key) => navigate(`/dashboard/tasks/${key}`);
  const closeTask = () => navigate("/dashboard/tasks");

  const quickUpdate = async (task, patch) => {
    setTasks(ts => ts.map(t => (t.id === task.id ? { ...t, ...patch } : t)));
    try { await api.updateTask(task.key, patch); } catch (e) {
      setError(e?.response?.data?.detail || "Failed to update task.");
      load();
    }
  };

  const onSort = (col) => setSort(s => ({ col, dir: s.col === col && s.dir === "asc" ? "desc" : "asc" }));

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = (tasks || []).filter(t =>
      (!hideClosed || !CLOSED.has(t.status) || statusF.has(t.status))
      && (!statusF.size || statusF.has(t.status))
      && (!priorityF.size || priorityF.has(t.priority))
      && (!assigneeF.size || assigneeF.has(t.assignee_user_id || "unassigned"))
      && (!q || t.title.toLowerCase().includes(q) || t.key.toLowerCase().includes(q) || (t.labels || []).some(l => l.toLowerCase().includes(q)))
    );
    const val = {
      key: t => t.number,
      title: t => t.title.toLowerCase(),
      status: t => STATUS_RANK[t.status],
      priority: t => PRIORITY_RANK[t.priority],
      impressions: t => t.impressions,
      due: t => t.due_date || "9999-12-31",
      updated: t => t.updated_at,
    }[sort.col] || (t => t.number);
    const dir = sort.dir === "asc" ? 1 : -1;
    return rows.sort((a, b) => (val(a) > val(b) ? dir : val(a) < val(b) ? -dir : b.number - a.number));
  }, [tasks, search, statusF, priorityF, assigneeF, hideClosed, sort]);

  const countBy = (fn) => (tasks || []).reduce((acc, t) => { const k = fn(t); acc[k] = (acc[k] || 0) + 1; return acc; }, {});
  const filtersOn = search || statusF.size || priorityF.size || assigneeF.size;
  const resetFilters = () => { setSearch(""); setStatusF(new Set()); setPriorityF(new Set()); setAssigneeF(new Set()); };
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="px-6 py-8 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <ListChecks size={16} strokeWidth={2} className="text-zinc-900" />
          <div>
            <h1 className="text-base font-bold text-zinc-900">Tasks</h1>
            <p className="text-[11px] text-zinc-400 mt-0.5">
              Who fixes what — tasks from Search Performance queries and suggested issues
            </p>
          </div>
        </div>
        {/* Any member can create a task; editing stays team-only. */}
        <button
          onClick={() => setPickerOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white rounded-lg"
          style={{ backgroundColor: PRIMARY }}
        >
          <Plus size={12} /> New task
        </button>
      </div>

      <div className="flex gap-0.5 mb-5 bg-zinc-100 rounded-lg p-0.5 w-fit">
        {[
          { key: "tasks", label: "Tasks", count: tasks ? tasks.filter(t => !CLOSED.has(t.status)).length : null },
          { key: "plan", label: "Map plan", count: planCount },
          { key: "sitemap", label: "Sitemap plan", count: sitemapCount },
          { key: "suggested", label: "Suggested", count: suggestedCount },
        ].map(({ key, label, count }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-md transition-colors"
            style={tab === key
              ? { backgroundColor: "#fff", color: "#18181b", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }
              : { backgroundColor: "transparent", color: "#71717a" }}
          >
            {label}
            {count != null && <span className="text-[10px] text-zinc-400 tabular-nums">{count}</span>}
          </button>
        ))}
      </div>

      {notice && (
        <div className="mb-3 px-4 py-2.5 bg-green-50 border border-green-100 rounded-xl text-xs text-green-700">{notice}</div>
      )}
      {error && (
        <div className="flex items-center gap-2 mb-3 px-4 py-2.5 bg-red-50 border border-red-100 rounded-xl text-xs text-red-600">
          {error}
          <button onClick={() => setError("")} className="ml-auto text-red-300 hover:text-red-600"><X size={12} /></button>
        </div>
      )}

      {/* Kept mounted so the tab counts show before a tab is opened. */}
      <div className={tab === "plan" ? "" : "hidden"}>
        <MapPlanTab
          tasks={tasks}
          onCount={setPlanCount}
          onCreated={(made) => { setNotice(`Created ${made.map(t => t.key).join(", ")}`); load(); }}
        />
      </div>
      <div className={tab === "sitemap" ? "" : "hidden"}>
        <SitemapPlanTab
          tasks={tasks}
          onCount={setSitemapCount}
          onCreated={(made) => { setNotice(`Created ${made.map(t => t.key).join(", ")}`); load(); }}
        />
      </div>
      <div className={tab === "suggested" ? "" : "hidden"}>
        <SuggestedTab
          canEdit={canEdit}
          canCreate
          onCount={setSuggestedCount}
          onAccepted={(task) => { setNotice(`Created ${task.key}`); load(); }}
        />
      </div>

      {tab === "tasks" && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="relative w-56">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Filter tasks…"
                className="w-full h-8 pl-8 pr-3 text-xs border border-zinc-200 bg-white rounded-lg focus:outline-none focus:ring-2"
                style={{ "--tw-ring-color": PRIMARY }}
              />
            </div>
            <FacetFilter
              title="Status"
              selected={statusF}
              onChange={setStatusF}
              counts={countBy(t => t.status)}
              options={Object.entries(STATUS_META).map(([v, m]) => ({ value: v, label: m.label, icon: m.icon, color: m.color }))}
            />
            <FacetFilter
              title="Priority"
              selected={priorityF}
              onChange={setPriorityF}
              counts={countBy(t => t.priority)}
              options={Object.entries(PRIORITY_META).map(([v, m]) => ({ value: v, label: m.label, icon: m.icon, color: m.color }))}
            />
            <FacetFilter
              title="Assignee"
              selected={assigneeF}
              onChange={setAssigneeF}
              counts={countBy(t => t.assignee_user_id || "unassigned")}
              options={[
                { value: "unassigned", label: "Unassigned", avatar: <span className="mr-2"><MemberAvatar member={null} size={16} /></span> },
                ...members.map(m => ({ value: m.user_id, label: m.name, avatar: <span className="mr-2"><MemberAvatar member={m} size={16} /></span> })),
              ]}
            />
            {filtersOn ? (
              <button onClick={resetFilters} className="inline-flex items-center gap-1 h-8 px-2 text-xs font-medium text-zinc-500 hover:text-zinc-900">
                Reset <X size={12} />
              </button>
            ) : null}
            <label className="ml-auto inline-flex items-center gap-1.5 text-xs text-zinc-500 cursor-pointer select-none">
              <input type="checkbox" checked={hideClosed} onChange={e => setHideClosed(e.target.checked)} className="rounded border-zinc-300" />
              Hide done & canceled
            </label>
          </div>

          <div className="bg-white rounded-xl border border-zinc-100 shadow-sm overflow-hidden">
            <Table className="text-[13px]">
              <TableHeader className="bg-zinc-50">
                <TableRow className="hover:bg-zinc-50">
                  <SortHead col="key" label="Key" sort={sort} onSort={onSort} className="w-20" />
                  <SortHead col="title" label="Title" sort={sort} onSort={onSort} />
                  <SortHead col="status" label="Status" sort={sort} onSort={onSort} className="w-32" />
                  <SortHead col="priority" label="Priority" sort={sort} onSort={onSort} className="w-28" />
                  <TableHead className="w-12 text-[10px] tracking-[0.1em] uppercase font-bold text-zinc-400">Who</TableHead>
                  <SortHead col="impressions" label="Queries" sort={sort} onSort={onSort} className="w-32" />
                  <SortHead col="due" label="Due" sort={sort} onSort={onSort} className="w-24" />
                  <SortHead col="updated" label="Updated" sort={sort} onSort={onSort} className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks === null && (
                  <TableRow><TableCell colSpan={8} className="py-10 text-center text-zinc-400">Loading…</TableCell></TableRow>
                )}
                {tasks && visible.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="py-10 text-center text-zinc-400">
                      {tasks.length === 0
                        ? "No tasks yet. Select queries in Search Performance → Create task, or accept a suggestion."
                        : "No tasks match these filters."}
                    </TableCell>
                  </TableRow>
                )}
                {visible.map(t => (
                  <TableRow key={t.id} onClick={() => openTask(t.key)} className="cursor-pointer">
                    <TableCell className="text-[11px] font-bold text-zinc-400 whitespace-nowrap">{t.key}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 min-w-0">
                        <ActionBadge action={t.action_type} />
                        <span className={`font-medium truncate ${CLOSED.has(t.status) ? "text-zinc-400 line-through" : "text-zinc-900"}`}>{t.title}</span>
                        {(t.labels || []).map(l => (
                          <span key={l} className="px-1.5 py-px rounded-full border border-zinc-200 text-[10px] text-zinc-500 whitespace-nowrap">{l}</span>
                        ))}
                        {t.comment_count > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-zinc-400">
                            <MessageSquare size={10} /> {t.comment_count}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      {canEdit ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger className="rounded px-1 py-0.5 hover:bg-zinc-100"><StatusLabel status={t.status} /></DropdownMenuTrigger>
                          <DropdownMenuContent align="start">
                            {Object.keys(STATUS_META).map(s => (
                              <DropdownMenuItem key={s} onSelect={() => quickUpdate(t, { status: s })}><StatusLabel status={s} /></DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : <StatusLabel status={t.status} />}
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      {canEdit ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger className="rounded px-1 py-0.5 hover:bg-zinc-100"><PriorityLabel priority={t.priority} /></DropdownMenuTrigger>
                          <DropdownMenuContent align="start">
                            {Object.keys(PRIORITY_META).map(p => (
                              <DropdownMenuItem key={p} onSelect={() => quickUpdate(t, { priority: p })}><PriorityLabel priority={p} /></DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : <PriorityLabel priority={t.priority} />}
                    </TableCell>
                    <TableCell><MemberAvatar member={byId[t.assignee_user_id]} size={22} /></TableCell>
                    <TableCell className="text-xs text-zinc-600 tabular-nums whitespace-nowrap">
                      {t.query_count ? (
                        <>{t.query_count} <span className="text-zinc-400">· {Number(t.impressions).toLocaleString()} impr</span></>
                      ) : <span className="text-zinc-300">—</span>}
                    </TableCell>
                    <TableCell className={`text-xs whitespace-nowrap ${t.due_date && t.due_date < today && !CLOSED.has(t.status) ? "text-red-500 font-bold" : "text-zinc-600"}`}>
                      {t.due_date ? new Date(`${t.due_date}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : <span className="text-zinc-300">—</span>}
                    </TableCell>
                    <TableCell className="text-xs text-zinc-400 whitespace-nowrap">{relativeTime(t.updated_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {tasks && tasks.length > 0 && (
            <p className="mt-2 text-[11px] text-zinc-400">{visible.length} of {tasks.length} tasks</p>
          )}
        </>
      )}

      <TaskPicker
        canAddToExisting={canEdit}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        members={members}
        onDone={(task, message) => { setNotice(message); load(); openTask(task.key); }}
      />
      <TaskDrawer
        taskKey={openKey || null}
        onClose={closeTask}
        members={members}
        byId={byId}
        canEdit={canEdit}
        onChanged={load}
      />
    </div>
  );
}
